// Adds a global `id` attribute to all structural container nodes so
// TipTap/ProseMirror keeps stable ids across edits and saves.
// Also auto-assigns and deduplicates ids on creation to stabilize diffs.

import { Extension } from '@tiptap/core'
import { Plugin } from 'prosemirror-state'
import { v4 as uuidv4 } from 'uuid'

// Must match CONTAINER_TYPES_FOR_IDS in normalizeForSave.js
const TYPES = [
  // text-bearing blocks
  'paragraph',
  'heading',
  'blockquote',
  // lists
  'bulletList',
  'orderedList',
  'listItem',
  // tables
  'table',
  'tableRow',
  'tableCell',
  'tableHeader',
  // embeds
  'video',
]

export default Extension.create({
  name: 'globalIds',

  addGlobalAttributes() {
    return TYPES.map(type => ({
      types: [type],
      attributes: {
        id: {
          default: null,
          // Read `id` from HTML if present (e.g., from pasted or server HTML)
          parseHTML: el => (el.getAttribute ? el.getAttribute('id') : null),
          // Write `id` back to HTML to persist through serialization
          renderHTML: attrs => (attrs.id ? { id: attrs.id } : {}),
        },
      },
    }))
  },

  // Ensure every new node of our TYPES has a unique, stable id immediately in the live doc.
  // Also repairs duplicates introduced by copy/paste or programmatic inserts.
  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction(transactions, oldState, newState) {
          if (!transactions.some(t => t.docChanged)) return null

          const seen = new Set()
          let tr = newState.tr
          let changed = false

          newState.doc.descendants((node, pos) => {
            const typeName = node.type?.name
            if (!TYPES.includes(typeName)) return

            let id = node.attrs?.id || null
            if (!id || seen.has(id)) {
              id = uuidv4()
              tr = tr.setNodeMarkup(pos, node.type, { ...node.attrs, id }, node.marks)
              changed = true
            }
            seen.add(id)
          })

          return changed ? tr : null
        },
      }),
    ]
  },
})